import React, { useEffect, useState } from 'react';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { setCurrentUser } from '../../context/actions';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../context/alert/AlertProvider';
import apiClient from '../../utils/apiClient';

function Login() {
  const { dispatch } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const redirectMessage = sessionStorage.getItem('auth_redirect_message');
    if (redirectMessage) {
      try {
        const message = JSON.parse(redirectMessage);
        showAlert(message);
        sessionStorage.removeItem('auth_redirect_message');
      } catch (error) {
        console.error('Error parsing redirect message:', error);
      }
    }

    const signupSuccess = sessionStorage.getItem('signupSuccess');
    if (signupSuccess) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        sessionStorage.removeItem('signupSuccess');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', formData);
      const data = response.data;

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      dispatch(setCurrentUser(data.user));
      
      const redirectPath = location.state?.from || '/';
      navigate(redirectPath);
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t("auth.error.login.invalid"));
      } else {
        setError(err.response?.data?.message || t("auth.error.login.failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main className="min-h-screen -mt-16 flex flex-col items-center pt-16 bg-gray-100 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
            {t("auth.login.title")}
          </h2>
        </div>

        {error && (
          <div className='mt-4 p-3 bg-red-50 text-red-500 rounded-lg text-sm'>
            {error}
          </div>
        )}

        {showSuccess && (
          <div className="mt-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm flex items-center gap-2 transition-opacity duration-500 opacity-100">
            <div className="h-2 w-2 bg-green-600 rounded-full"></div>
            {sessionStorage.getItem('signupSuccess')}
          </div>
        )}

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="relative">
              <label htmlFor="email" className="sr-only">
                {t("auth.login.email")}
              </label>
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t("auth.login.email")}
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="sr-only">
                {t("auth.login.password")}
              </label>
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t("auth.login.password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                preventScrollReset={true}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t("auth.login.forgotPassword")}
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {loading ? t("auth.login.signingIn") : t("auth.login.button")}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">
                {t("auth.login.noAccount")}
              </span>
            </div>
          </div>
          <Link to="/signup">
            <button
              className="mt-4 w-full flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("auth.login.createAccount")}
            </button>
          </Link>
        </div>
      </div>
    </main>
  );
}

export default Login;